import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"

dayjs.extend(customParseFormat)

/**
 * Format adapters between the wizard's internal representation and the backend
 * contract for availability schedules.
 *
 * - Days: UI stores full lowercase names ("monday"); backend uses 3-letter
 *   abbreviations ("mon").
 * - disabled_times: UI stores 12h "hh:mm A" ("02:45 AM"); backend uses 24h
 *   "HH:mm" ("14:45"). (start_time/end_time are already stored 24h, so they
 *   only need a key rename, handled in the mappers.)
 */

const DAY_FULL_TO_ABBR = {
  sunday: "sun",
  monday: "mon",
  tuesday: "tue",
  wednesday: "wed",
  thursday: "thu",
  friday: "fri",
  saturday: "sat",
}
const DAY_ABBR_TO_FULL = Object.fromEntries(
  Object.entries(DAY_FULL_TO_ABBR).map(([full, abbr]) => [abbr, full])
)

export const daysToApi = (days = []) => days.map((day) => DAY_FULL_TO_ABBR[day] ?? day)
export const daysFromApi = (days = []) => days.map((day) => DAY_ABBR_TO_FULL[day] ?? day)

export const timesTo24h = (times = []) =>
  times.map((time) => (time ? dayjs(time, ["hh:mm A", "HH:mm"]).format("HH:mm") : time))
export const timesTo12h = (times = []) =>
  times.map((time) => (time ? dayjs(time, ["HH:mm", "hh:mm A"]).format("hh:mm A") : time))

/**
 * Regenerate the enabled start-time labels (12h, for list display) from a
 * schedule's rule, since the backend doesn't persist start_times.
 * @param {string} fromTime  - "HH:mm"
 * @param {string} toTime    - "HH:mm"
 * @param {number} interval  - minutes
 * @param {string[]} disabledTimes12h - times to exclude, in "hh:mm A"
 */
export const generateStartTimes = (fromTime, toTime, interval, disabledTimes12h = []) => {
  if (!fromTime || !toTime || !interval) return []
  const start = dayjs(fromTime, ["HH:mm", "hh:mm A"])
  const end = dayjs(toTime, ["HH:mm", "hh:mm A"])
  if (!start.isValid() || !end.isValid()) return []
  const disabled = new Set(disabledTimes12h)
  const times = []
  let current = start.clone()
  // "To" is the closing boundary, not a start slot — the last start is strictly before
  // it (kept in sync with generateTimes in AddAvailabilityScheduleModal).
  while (current.isBefore(end)) {
    const label = current.format("hh:mm A")
    if (!disabled.has(label)) times.push(label)
    current = current.add(interval, "minute")
  }
  return times
}
